import { useCallback, useRef, useState } from 'react';
import { updateColumns } from './utils';
import { handleDragOver, handleDragLeave, handleDrop as originalHandleDrop } from './Handlers';
import { SAVED_PLACES_COLUMN_ID } from './Constants';
import '../../../styles/components/itinerary/DragAndDrop.css';
import { usePlaceContext } from '../PlaceContext';
import useApi from '../../../Api';
import { useAuth } from '../../../AuthContext';
import { toast } from 'react-toastify';

const useDragAndDrop = (setColumns, setColumnsToSave, savePlace, savedPlaces, selectedItinerary, savedPlacesRef) => {
  const liveRegion = document.getElementById('aria-live-region');
  const scrollRef = useRef(null);
  const [highlightedColumnId, setHighlightedColumnId] = useState(null);
  const { addColumnMarker, removeColumnMarker, removePlaceFromSaved, stateRef } = usePlaceContext();
  const { token } = useAuth();
  const apiClient = useApi();

  const setDragData = (e, place, columnId, placeIndex) => {
    const data = {
      placeId: place.id,
      columnId,
      placeIndex,
      placeData: place,
      photoUrl: place.photoUrl,
    };
    e.dataTransfer.setData('text/plain', JSON.stringify(data));
    if (liveRegion) liveRegion.textContent = `Started dragging ${place.name}`;
  };

  const handleRemoveWidget = useCallback(async (sourceColumnId, place, isSameColumn = false, isDropAction = false) => {
    if (sourceColumnId === SAVED_PLACES_COLUMN_ID) {
      if (isSameColumn === false) {
        removePlaceFromSaved(place);
      }
      return;
    }
    const itinerary = { ...stateRef.current.selectedItinerary };
    const prevColumns = itinerary.columns;
    const column = prevColumns.find(col => col.id === sourceColumnId);
    const position = column ? column.items.findIndex(item => item.id === place.id) : -1;
    if (position === -1) return;
    let updatedColumns;
    setColumns((prevColumns) => {
      updatedColumns = updateColumns(prevColumns, sourceColumnId, place, 'remove');
      itinerary.columns = updatedColumns;
      setColumnsToSave(updatedColumns);
      if (liveRegion) liveRegion.textContent = `Removed widget from ${sourceColumnId}`;
      return updatedColumns;
    });
    if (!isSameColumn) {
      removeColumnMarker(place);
    }
    try {
      const itineraryId = itinerary.id;
      const payload = {
        place_id: place.place_id || null,
        google_place_id: place.google_place_id || null,
        position: position
      };
      await apiClient.delete(`/itineraries/${itineraryId}/columns/${sourceColumnId}/places`, { data: payload }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error removing place from column:', error);
    }
  }, [setColumns, setColumnsToSave, liveRegion, removePlaceFromSaved, removeColumnMarker, stateRef, apiClient, token]);

  const handleAddWidget = useCallback(async (destinationColumnId, place, index, isSameColumn = false) => {
    const prevColumns = stateRef.current.selectedItinerary.columns;
    if (!isSameColumn && destinationColumnId !== SAVED_PLACES_COLUMN_ID) {
      const column = prevColumns.find(col => col.id === destinationColumnId);
      if (column) {
        const exists = column.items.some(item =>
          item.name === place.name &&
          item.latitude === place.latitude &&
          item.longitude === place.longitude
        );
        if (!exists) addColumnMarker(place, destinationColumnId);
      }
    }
    setColumns((prevColumns) => {
      const updatedColumns = updateColumns(prevColumns, destinationColumnId, place, 'add', index);
      setColumnsToSave(updatedColumns);
      if (liveRegion) liveRegion.textContent = `Added widget to ${destinationColumnId}`;
      return updatedColumns;
    });
    if (destinationColumnId === SAVED_PLACES_COLUMN_ID) return;
    try {
      const itineraryId = stateRef.current.selectedItinerary.id;
      const position = index ?? prevColumns.find(col => col.id === destinationColumnId).items.length;
      const payload = {
        place_id: place.place_id || null,
        google_place_id: place.google_place_id || null,
        position: position
      };
      await apiClient.post(`/itineraries/${itineraryId}/columns/${destinationColumnId}/places`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error adding place to column:', error);
    }
  }, [setColumns, setColumnsToSave, liveRegion, addColumnMarker, stateRef, apiClient, token]);

  const handleDrop = useCallback((e, destinationColumnId) => {
    const dropDataString = e.dataTransfer.getData('text/plain');
    if (!dropDataString) return;
    const dropData = JSON.parse(dropDataString);
    const sourceColumnId = dropData.columnId;
    if (destinationColumnId !== sourceColumnId && stateRef.current.selectedItinerary) {
      const itineraryId = stateRef.current.selectedItinerary.id;
      if (destinationColumnId === SAVED_PLACES_COLUMN_ID) {
        const length = stateRef.current.savedPlaces[itineraryId]?.length || 0;
        if (length >= 15) {
          toast.error('The maximum number of places in Saved Places is 15.', {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          return;
        }
      } else {
        const col = stateRef.current.selectedItinerary.columns.find(c => c.id === destinationColumnId);
        if (col && col.items.length >= 10) {
          toast.error('The maximum number of places in a column is 10.', {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          return;
        }
      }
    }
    originalHandleDrop(
      e,
      destinationColumnId,
      highlightedColumnId,
      setHighlightedColumnId,
      liveRegion,
      savedPlacesRef,
      setColumns,
      setColumnsToSave,
      savePlace,
      savedPlaces,
      selectedItinerary,
      handleRemoveWidget,
      handleAddWidget
    );
  }, [
    highlightedColumnId,
    setHighlightedColumnId,
    liveRegion,
    savedPlacesRef,
    setColumns,
    setColumnsToSave,
    savePlace,
    savedPlaces,
    selectedItinerary,
    handleRemoveWidget,
    handleAddWidget,
    stateRef
  ]);

  return {
    handleDragOver: (e, columnId) => handleDragOver(e, columnId, highlightedColumnId, setHighlightedColumnId, savedPlacesRef, liveRegion, scrollRef),
    handleDragLeave: (e, columnId) => handleDragLeave(e, columnId, setHighlightedColumnId, liveRegion, savedPlacesRef),
    handleDrop,
    handleRemoveWidget,
    handleAddWidget,
    setDragData,
    scrollRef,
  };
};

export { useDragAndDrop, SAVED_PLACES_COLUMN_ID };
